import { useCallback, useEffect, useState } from 'react';
import type { ReportBundle } from '../../shared/types.js';
import type { ReportQuery } from '../../shared/schemas.js';
import { api } from '../api/client.js';
import { GapReport } from './report/GapReport.js';
import { MatrixView } from './report/MatrixView.js';
import { OnePager } from './report/OnePager.js';
import { PrintView } from './report/PrintView.js';
import { reportToMarkdown } from '../report/markdown.js';

export function ReportView() {
  const [query, setQuery] = useState<ReportQuery>({});
  const [report, setReport] = useState<ReportBundle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'gap' | 'matrix' | 'onepager'>('gap');

  const load = useCallback(() => {
    api
      .getReport(query)
      .then((data) => {
        setError(null);
        setReport(data);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load report'));
  }, [query]);

  const downloadMarkdown = useCallback(() => {
    if (!report) return;
    const blob = new Blob([reportToMarkdown(report)], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rung-levelling-case.md';
    a.click();
    URL.revokeObjectURL(url);
  }, [report]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="error">{error}</p>;
  if (!report) return <p className="muted">Building report…</p>;

  return (
    <div className="report">
      <div className="report-controls">
        <label className="filter-date">
          from
          <input
            type="date"
            value={query.from ?? ''}
            onChange={(e) => setQuery((q) => ({ ...q, from: e.target.value || undefined }))}
          />
        </label>
        <label className="filter-date">
          to
          <input
            type="date"
            value={query.to ?? ''}
            onChange={(e) => setQuery((q) => ({ ...q, to: e.target.value || undefined }))}
          />
        </label>
        <label className="filter-date">
          <input
            type="checkbox"
            checked={query.include_drafts === 'true'}
            onChange={(e) => setQuery((q) => ({ ...q, include_drafts: e.target.checked ? 'true' : undefined }))}
          />
          include drafts
        </label>
        <button type="button" className="ghost small" onClick={downloadMarkdown}>
          Export Markdown
        </button>
        <button type="button" className="ghost small" onClick={() => window.print()}>
          Print / PDF
        </button>
      </div>
      <p className="muted">
        Assigned {report.config.assigned_level} · target {report.config.target_level} ·{' '}
        {report.summary.dimensions_meeting_target}/{report.summary.dimensions_total} dimensions ≥ target ·{' '}
        {report.period.from} → {report.period.to}
      </p>
      <div className="report-subtabs">
        <button type="button" className={tab === 'gap' ? 'tab active' : 'tab'} onClick={() => setTab('gap')}>
          Gap
        </button>
        <button type="button" className={tab === 'matrix' ? 'tab active' : 'tab'} onClick={() => setTab('matrix')}>
          Matrix
        </button>
        <button type="button" className={tab === 'onepager' ? 'tab active' : 'tab'} onClick={() => setTab('onepager')}>
          One-pager
        </button>
      </div>

      {tab === 'gap' && <GapReport report={report} />}
      {tab === 'matrix' && <MatrixView report={report} />}
      {tab === 'onepager' && <OnePager report={report} />}
      <PrintView report={report} />
    </div>
  );
}
